import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import {
  getRepeatSummaryTaskExecutionList,
  getReportViewRecords,
  gptExecutionsReport,
  setReportViewRecords,
} from '@op/services';
import { DBO_TM, DBO_U } from '@op/utils/format';
import { objToUrl } from '@op/utils/url';
import { useEffect, useMemo, useState } from 'react';
import { getLocale, history } from 'umi';

export function useAiSaveName({
  getSaveName,
  setSaveName,
}: {
  getSaveName: (timeTitle: string) => string;
  setSaveName: (value: string) => void;
}) {
  const [timeTitle, setTimeTitle] = useState('');

  useEffect(() => {
    setSaveName(getSaveName(timeTitle));
  }, [timeTitle]);

  function filterOnChange(value: any) {
    const titleTime = DBO_TM.timeTitle(value);
    if (timeTitle !== titleTime) {
      setTimeTitle(titleTime);
    }
  }

  return { filterOnChange };
}

export function useRepeatTaskExecutionList({
  taskId,
  reportId,
  needRequest,
  defaultValue: defaultValueProps,
}: any) {
  const [data, setData] = useState<any>([]);
  const [error, setError] = useState(false);
  const [defaultValue, setDefaultValue] = useState({});
  const { loading, wrapperRequestTile } = useCancelRequestHook({
    defaultLoading: true,
  });

  function requestTaskReportList() {
    if (!taskId) return;
    setError(false);
    setData([]);
    wrapperRequestTile(getRepeatSummaryTaskExecutionList, {
      repeat_task_id: taskId,
      page: 1,
      page_size: 10000,
      ...(reportId ? { execution_id: reportId } : {}),
    })
      .then(data => {
        const list = data?.list || [];
        const successList = list?.filter(
          (val: { status: string }) => val.status === 'success',
        );
        // 默认为列表第一个
        let item = list?.[0];
        // 如果有成功的
        if (successList?.length > 0) {
          // 如果和默认值相同的就用默认值，否则用第一个
          if (defaultValueProps?.start_time && defaultValueProps?.end_time) {
            item = defaultValueProps;
          } else {
            item = successList[0];
          }
        }
        const { start_time, end_time } = item || {};
        if (start_time && end_time) {
          setDefaultValue({ start_time, end_time });
        }
        setData(list);
      })
      .catch(() => {
        setError(true);
      });
  }

  useEffect(() => {
    if (needRequest) {
      setData([]);
      requestTaskReportList();
    }
  }, [taskId, reportId, needRequest]);

  return { data, loading, error, requestTaskReportList, defaultValue };
}

const dateTypeMap: any = {
  month: 'monthly',
  day: 'daily',
  hour: 'hourly',
  week: 'daily',
};

export function useCustomInsightReportList({
  gameInfo,
  task,
  defaultLanguage,
  defaultValue: defaultValueProps,
}: {
  gameInfo: any;
  task: any;
  defaultLanguage?: string;
  defaultValue?: any;
}) {
  function getDefaultValue() {
    return defaultLanguage || (getLocale() === 'zh-CN' ? 'zh' : 'en');
  }

  const [language, setLanguage] = useState(getDefaultValue());

  const { filterParams, filterDateType } = useMemo(() => {
    const filter = {
      gameInfo,
      taskId: task?.id,
      dateType: task?.interval_unit,
      needRequest: true,
      defaultValue: defaultValueProps,
    };

    const filterDateType = dateTypeMap[task?.interval_unit] || 'daily';

    return {
      filterParams: filter,
      filterDateType,
    };
  }, [gameInfo, task]);

  const { data, loading, defaultValue } =
    useRepeatTaskExecutionList(filterParams);

  const isEmpty = useMemo(() => {
    return (data || []).length === 0;
  }, [data]);

  const gptLanguage = useMemo(() => {
    return language === 'zh' ? 'zh-CN' : 'en-US';
  }, [language]);

  return {
    data,
    loading,
    language,
    setLanguage,
    isEmpty,
    filterDateType,
    defaultValue,
    gptLanguage,
  };
}

const menuDateTypeMap: any = {
  1: 'daily',
  2: 'daily',
  3: 'monthly',
  11: 'hourly',
  12: 'hourly',
};
export function useMenuInsightReportList({
  gameInfo,
  defaultParams,
  defaultLanguage,
}: {
  gameInfo: any;
  defaultParams: any;
  defaultLanguage?: string | null;
}) {
  function getDefaultValue() {
    return defaultLanguage || getLocale() === 'zh-CN' ? 'zh' : 'en';
  }

  const [language, setLanguage] = useState(getDefaultValue());
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { filterParams, filterDateType } = useMemo(() => {
    const filter = {
      ...gameInfo,
      kind: Number(defaultParams.kind),
      type: Number(defaultParams.type),
      page: 1,
      page_size: 10000,
    };

    const filterDateType = menuDateTypeMap[defaultParams.type] || 'daily';

    return {
      filterParams: filter,
      filterDateType,
    };
  }, [defaultParams]);

  function requestData() {
    setLoading(true);
    gptExecutionsReport(filterParams)
      .then((data: any) => {
        setData(data?.list || []);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    requestData();
  }, []);

  const isEmpty = useMemo(() => {
    return (data || []).length === 0;
  }, [data]);

  return {
    data,
    loading,
    language,
    setLanguage,
    isEmpty,
    filterDateType,
  };
}

export function useAiReportNewTag({
  data,
  needRequest = true,
  type = 'repeat_summary_task',
}: {
  data?: Array<any>;
  needRequest?: boolean;
  type?: 'repeat_summary_task' | 'repeat_keyword_analysis_task';
}) {
  const [loading, setLoading] = useState(false);
  // 有记录代表无数据
  const [noNewTagsReport, setNoNewTagsReport] = useState<{
    [key: string]: boolean;
  }>({});

  function getReportViews(ids: Array<string>) {
    setLoading(true);
    getReportViewRecords({
      object_types: [type],
      object_ids: ids,
    })
      .then((data: Array<any>) => {
        if (data && Array.isArray(data)) {
          const reportViewList = data.reduce((pre, cur) => {
            return { ...pre, [cur.object_id]: true };
          }, {});

          setNoNewTagsReport(reportViewList);
        }
      })
      .catch((err: any) => {
        console.error('Failed to get report view records:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function setReportView(id: string) {
    setReportViewRecords({
      object_type: 'repeat_summary_task',
      object_ids: [id],
      value: '1',
    }).then(() => {
      setNoNewTagsReport({ ...noNewTagsReport, [id]: true });
    });
  }

  useEffect(() => {
    if (data && data.length > 0 && needRequest) {
      const ids = data.map(item => String(item.id));
      getReportViews(ids);
    }
  }, [data, needRequest]);

  return {
    loading,
    noNewTagsReport,
    setReportView,
  };
}

export const updateURL = ({
  updateParams = {},
  filter = [],
}: {
  updateParams?: any;
  filter?: Array<string>;
}) => {
  // 用实时 location.search 解析 query，而非闭包里传入的 location.query。
  // ReportGpt/CustomGpt 的 confirm 回调捕获的 location 是 render 时的快照，
  // 当它在 tab 刚切换后触发时，query.tab 仍是上一个 tab 的值，
  // 直接 spread 会把 URL 污染成 ?tab=<上一个>，导致复制链接打开后定位到错误的 tab。

  const query = DBO_U.queryObj(location.search) || {};
  const data = {
    ...query,
    ...(updateParams || {}),
  };

  filter.forEach(key => {
    delete data[key];
  });

  const url = objToUrl(data);
  let pathName = location.pathname;
  if (pathName.startsWith("/v2")) {
    pathName = pathName.split("/v2")[1];
  }
  history.replace(`${pathName}?${url}`);
};
